/**
 * PricingPage.jsx — Enumé Access Tiers
 * Place at: frontend/src/pages/PricingPage.jsx
 *
 * Decorative for now — Stripe is not yet configured (see payment_config.py).
 * Buttons are disabled / route to a "coming soon" state rather than checkout.
 *
 * Exists to solve a real confusion: handing someone a raw Partner API key
 * (X-API-Key, JSON, webhooks) assumes they're a developer. Most people —
 * e.g. staff at an architecture firm — just want to log in, join a cohort,
 * and upload work. This page makes that distinction explicit up front:
 * Free/Starter need no code at all; Pro/Enterprise are for teams building
 * their own integration against the Partner API (see /api for docs).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, Code2, MousePointerClick, Sparkles, ArrowRight,
  Mail, GraduationCap, Building2, Rocket, Globe2,
} from 'lucide-react';

// ============================================================================
// TIER DATA
// ============================================================================

const NO_CODE_TIERS = [
  {
    id: 'free',
    name: 'Free',
    icon: MousePointerClick,
    price: '$0',
    cadence: 'forever',
    tagline: 'Join a class or team space and start submitting work.',
    features: [
      'Join unlimited cohorts',
      'Submit projects to the cosmic map',
      'View lineage and fork history',
      'Standard map & explore access',
    ],
    cta: 'Get started',
    accent: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    icon: GraduationCap,
    price: '$0',
    cadence: 'for instructors & teams',
    tagline: 'Run your own classes or studio spaces, no setup required.',
    features: [
      'Everything in Free',
      'Create unlimited cohorts',
      'Custom taxonomy labels',
      'Member management & join links',
    ],
    cta: 'Create a class',
    accent: false,
  },
];

const DEVELOPER_TIERS = [
  {
    id: 'pro',
    name: 'Pro',
    icon: Rocket,
    price: '$0',
    cadence: 'during early access',
    tagline: 'Build your own tool on top of Enumé.',
    requestsPerDay: '100,000',
    features: [
      'Partner API key (100,000 requests/day)',
      'Webhooks — push, not polling',
      'Full lineage & search endpoints',
      'OAuth on behalf of your users',
    ],
    cta: 'Request access',
    accent: true,
    badge: 'Most chosen',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    icon: Building2,
    price: 'Let\u2019s talk',
    cadence: 'for institutions',
    tagline: 'Campus-wide or multi-team integrations.',
    requestsPerDay: '10,000,000',
    features: [
      'Everything in Pro',
      'Dedicated support contact',
      'Custom rate limits',
      'Institutional onboarding',
    ],
    cta: 'Contact us',
    accent: false,
  },
];

// ============================================================================
// TIER CARD
// ============================================================================

function TierCard({ tier, requiresCode }) {
  const Icon = tier.icon;

  return (
    <div
      className={`relative flex flex-col h-full p-6 rounded-2xl border transition-colors ${
        tier.accent
          ? 'border-purple-500/40 bg-gradient-to-b from-purple-500/[0.07] to-transparent'
          : 'border-zinc-800 bg-zinc-900/40'
      }`}
    >
      {tier.badge && (
        <span className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-400 text-zinc-950">
          {tier.badge}
        </span>
      )}

      <div className="flex items-center gap-2.5 mb-5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          tier.accent ? 'bg-purple-500/20 text-purple-300' : 'bg-zinc-800 text-zinc-400'
        }`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
          {tier.name}
        </span>
      </div>

      <div className="mb-1 flex items-baseline gap-2">
        <span className="text-3xl font-bold text-white tracking-tight">{tier.price}</span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">{tier.cadence}</p>

      <p className="text-sm text-zinc-300 mb-6 leading-relaxed">{tier.tagline}</p>

      {tier.requestsPerDay && (
        <div className="mb-5 px-3 py-2 rounded-lg bg-zinc-950/60 border border-zinc-800 font-mono text-xs text-zinc-400">
          {tier.requestsPerDay} requests/day
        </div>
      )}

      <ul className="space-y-2.5 mb-7 flex-1">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tier.accent ? 'text-purple-400' : 'text-zinc-600'}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        disabled
        title="Billing isn't connected yet — message us and we'll set you up directly."
        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-not-allowed ${
          tier.accent
            ? 'bg-purple-600/40 text-purple-200/70'
            : 'bg-zinc-800/60 text-zinc-500'
        }`}
      >
        {tier.cta}
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
      <p className="text-center text-[11px] text-zinc-600 mt-2">Coming soon</p>
    </div>
  );
}

// ============================================================================
// CODE-REQUIREMENT DIVIDER
// The one structural device on this page — it encodes something true about
// the tiers (whether you need to write code) rather than just decorating
// the gap between two rows of cards.
// ============================================================================

function RequirementDivider({ icon: Icon, label, sublabel }) {
  return (
    <div className="flex items-center gap-4 my-12">
      <div className="flex-1 h-px bg-zinc-800" />
      <div className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-zinc-800 bg-zinc-900">
        <Icon className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-xs font-medium text-zinc-300">{label}</span>
        <span className="text-xs text-zinc-600">— {sublabel}</span>
      </div>
      <div className="flex-1 h-px bg-zinc-800" />
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav className="border-b border-zinc-800/60 px-6 py-4 flex items-center justify-between sticky top-0 bg-zinc-950/90 backdrop-blur-sm z-50">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-lg font-bold tracking-tight">Enumé</span>
        </Link>
        <Link
          to="/api"
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
        >
          <Code2 className="w-3.5 h-3.5" />
          API docs
        </Link>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-4 text-center">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-5">
          Most people on Enumé<br className="hidden md:block" /> never write a line of code.
        </h1>
        <p className="text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
          A handful of teams build on top of us instead. Pick the one that's actually you —
          not the one that sounds more technical.
        </p>
      </section>

      {/* ── Tiers ───────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-8">

        <p className="text-center text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-6">
          Join, submit, manage a class — in the browser
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl mx-auto">
          {NO_CODE_TIERS.map(t => <TierCard key={t.id} tier={t} />)}
        </div>

        <RequirementDivider
          icon={Code2}
          label="Everything below needs a developer"
          sublabel="you'll get an API key, not a login link"
        />

        <p className="text-center text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-6">
          Build your own tool on top of Enumé
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl mx-auto">
          {DEVELOPER_TIERS.map(t => <TierCard key={t.id} tier={t} requiresCode />)}
        </div>

      </section>

      {/* ── Reassurance footnote ───────────────────────────────────── */}
      <section className="max-w-2xl mx-auto px-6 pb-20">
        <div className="flex items-start gap-3 p-4 rounded-xl border border-zinc-800 bg-zinc-900/30">
          <Globe2 className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-zinc-400 leading-relaxed">
            Not sure which one fits your team? If you just need people to log in and upload
            work, you want <span className="text-zinc-200 font-medium">Free</span> or{' '}
            <span className="text-zinc-200 font-medium">Starter</span> — no API key involved.
            Reach out and we'll set you up directly.
          </p>
        </div>
      </section>

      <div className="border-t border-zinc-800/60" />

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">Still deciding?</h2>
        <p className="text-zinc-400 text-base mb-8 max-w-xl mx-auto">
          Billing isn't live yet, so every tier above is free to use today.
          Tell us what you're trying to do and we'll point you to the right one.
        </p>
        <a
          href="mailto:support@enume.io?subject=Access Tier Question"
          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-medium px-6 py-3 rounded-lg transition-colors"
        >
          <Mail className="w-4 h-4" />
          support@enume.io
        </a>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t border-zinc-800/60 px-6 py-8">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-zinc-600 text-sm">&copy; 2026 Enumé. All rights reserved.</span>
          <div className="flex gap-6 text-sm text-zinc-600">
            <Link to="/terms"   className="hover:text-zinc-400 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-zinc-400 transition-colors">Privacy</Link>
            <Link to="/api"     className="hover:text-zinc-400 transition-colors">API</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}